// AuthContext — manages JWT bearer in localStorage + /me lookup.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { authPublicControllerLogin, userControllerMe } from '@dvvb/sdk';
import {
  getStoredToken,
  setStoredToken,
} from '../lib/api-client';
import { unwrap, ApiCallError } from '../lib/envelope';
import type { MeResponse, AppLoginResponse } from '../types/api';

export type AuthRole = 'owner' | 'staff' | 'admin' | 'none';

interface AuthState {
  status: 'loading' | 'unauth' | 'auth' | 'error';
  me: MeResponse | null;
  role: AuthRole;
  token: string | null;
  error: string | null;
}

interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function deriveRole(me: MeResponse | null): AuthRole {
  if (!me) return 'none';
  if (me.user.isPlatformAdmin) return 'admin';
  const owner = me.organizations.find((o) => o.role === 'owner');
  if (owner) return 'owner';
  const staff = me.organizations.find((o) => o.role === 'staff');
  if (staff) return 'staff';
  return 'none';
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(() => ({
    status: getStoredToken() ? 'loading' : 'unauth',
    me: null,
    role: 'none',
    token: getStoredToken(),
    error: null,
  }));

  const refresh = useCallback(async () => {
    const token = getStoredToken();
    if (!token) {
      setState({
        status: 'unauth',
        me: null,
        role: 'none',
        token: null,
        error: null,
      });
      return;
    }
    setState((s) => ({ ...s, status: 'loading', token, error: null }));
    try {
      const result = await userControllerMe();
      const me = unwrap<MeResponse>(result);
      setState({
        status: 'auth',
        me,
        role: deriveRole(me),
        token,
        error: null,
      });
    } catch (err) {
      const e = err as ApiCallError;
      // Bad token (401) — clear it.
      if (e.status === 401) {
        setStoredToken(null);
        setState({
          status: 'unauth',
          me: null,
          role: 'none',
          token: null,
          error: null,
        });
        return;
      }
      setState({
        status: 'error',
        me: null,
        role: 'none',
        token,
        error: e.message ?? '인증 정보를 불러오지 못했습니다.',
      });
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(async (email: string, password: string) => {
    setState((s) => ({ ...s, status: 'loading', error: null }));
    try {
      const result = await authPublicControllerLogin({
        body: { email, password } as never,
      });
      const data = unwrap<AppLoginResponse>(result);
      const token = data.userSessionToken;
      if (!token) {
        throw new ApiCallError('서버가 토큰을 반환하지 않았습니다.', {});
      }
      setStoredToken(token);
      // Now hit /me to populate role.
      await refresh();
    } catch (err) {
      const e = err as ApiCallError;
      setState({
        status: 'unauth',
        me: null,
        role: 'none',
        token: null,
        error: e.message ?? '로그인에 실패했습니다.',
      });
      throw e;
    }
  }, [refresh]);

  const logout = useCallback(() => {
    setStoredToken(null);
    setState({
      status: 'unauth',
      me: null,
      role: 'none',
      token: null,
      error: null,
    });
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, login, logout, refresh }),
    [state, login, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
